// The platform admin's customers and partners (#417, #1189): every one, and the act
// that makes one.
//
// The product is invitation-only for now (owner, 2026-09-12, on the Phase 3
// cut's Q2), so this is where a customer comes from: a platform admin names it,
// says whether it is a customer or a partner, and
// names the person who will own it; the owner's invitation goes by mail. The
// form mints one `creation_key` when it opens, so a second click or a reload
// mid-submit replays the first result — the answer says so — rather than
// making two.
//
// Each one also carries an "Enter" (#701): a platform admin selects it,
// or one of its workspaces, without a membership. The entry is audited as the
// platform's; `PlatformEntry.tsx` holds the chooser.

import { useRef, useState, type FormEvent } from "react";

import { creationKey, type Created, type OrgRow } from "../api/customers";
import { addressRefusal, messageOf } from "../api/http";
import type { SessionResponse } from "../api/session";
import { Chip, Empty, Field, Notice, PageIntro } from "./chrome";
import { orgTypeText } from "./ManagedBy";
import { OrgEntry, type Enter, type ListWorkspaces } from "./PlatformEntry";

export function OrganisationsPage({
  session,
  orgs,
  error,
  onCreate,
  listWorkspaces,
  onEnter,
}: {
  session: SessionResponse;
  orgs: OrgRow[] | null;
  error: string | null;
  onCreate: (body: {
    kind: "customer" | "partner";
    name: string;
    owner_email: string;
    workspace_name: string | null;
    creation_key: string;
  }) => Promise<Created>;
  listWorkspaces: ListWorkspaces;
  onEnter: Enter;
}) {
  if (!session.user.superadmin) {
    return (
      <>
        <PageIntro destination="organisations" />
        <section className="panel">
          <Empty title="This screen is the platform's">
            <p>Only a platform admin creates customers. Yours are on the Customers screen.</p>
          </Empty>
        </section>
      </>
    );
  }
  return (
    <>
      <PageIntro
        destination="organisations"
      />
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <CreateOrg onCreate={onCreate} />
      <section className="panel">
        <div className="panel__head">
          <h2>All customers and partners</h2>
          {orgs === null ? null : <Chip>{orgs.length} shown</Chip>}
        </div>
        {orgs === null && error === null ? (
          <p className="muted panel__body" role="status">
            Reading the platform's customers and partners…
          </p>
        ) : null}
        {orgs !== null && orgs.length === 0 ? (
          <Empty title="No customers or partners yet">
            <p>The form above makes the first one.</p>
          </Empty>
        ) : null}
        {orgs !== null && orgs.length > 0 ? (
          <div className="table-wrap" tabIndex={0}>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Type</th>
                  <th scope="col">Workspaces</th>
                  <th scope="col">Members</th>
                  <th scope="col">Invitations open</th>
                  <th scope="col">Created</th>
                  <th scope="col">Enter</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => (
                  <tr key={org.org_id}>
                    <td>
                      <strong>{org.name}</strong>
                      <small>
                        <code>{org.org_id}</code>
                      </small>
                    </td>
                    <td>{orgTypeText(org)}</td>
                    <td>{org.workspaces}</td>
                    <td>{org.members}</td>
                    <td>
                      {org.live_invites > 0 ? (
                        <Chip tone="warn">{org.live_invites} waiting</Chip>
                      ) : (
                        <span className="muted">none</span>
                      )}
                    </td>
                    <td>{org.created_at.slice(0, 10)}</td>
                    <td>
                      <OrgEntry org={org} listWorkspaces={listWorkspaces} onEnter={onEnter} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}

function CreateOrg({
  onCreate,
}: {
  onCreate: (body: {
    kind: "customer" | "partner";
    name: string;
    owner_email: string;
    workspace_name: string | null;
    creation_key: string;
  }) => Promise<Created>;
}) {
  const [key, setKey] = useState(creationKey);
  const [kind, setKind] = useState<"customer" | "partner">("customer");
  const [name, setName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [made, setMade] = useState<Created | null>(null);
  const emailInput = useRef<HTMLInputElement>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setEmailError(null);
    setBusy(true);
    try {
      const created = await onCreate({
        kind,
        name,
        owner_email: email,
        workspace_name: kind === "customer" && workspaceName.trim() !== "" ? workspaceName.trim() : null,
        creation_key: key,
      });
      setMade(created);
      // The next customer is a new act: a new key, an empty form.
      setKey(creationKey());
      setName("");
      setWorkspaceName("");
      setEmail("");
    } catch (failure) {
      const refused = addressRefusal(failure, "owner_email");
      if (refused === null) setError(messageOf(failure));
      else {
        setEmailError(refused);
        emailInput.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>Create a customer</h2>
      </div>
      <form className="panel__body" onSubmit={submit} aria-busy={busy} data-testid="create-org">
        {made === null ? null : (
          <Notice tone="good">
            {made.replayed
              ? `${made.name} already existed from an earlier click; nothing new was made.`
              : `${made.name} is created. An invitation is on its way to ${made.invite.email}.`}
          </Notice>
        )}
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        <p className="field">
          <label htmlFor="org-kind" className="field__label">
            Type
          </label>
          <select
            id="org-kind"
            className="input"
            value={kind}
            onChange={(event) => setKind(event.target.value === "partner" ? "partner" : "customer")}
          >
            <option value="customer">Customer, with its own workspace</option>
            <option value="partner">Partner, which invites its customers</option>
          </select>
        </p>
        <Field
          id="org-name"
          label="Name"
          name="name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        {kind === "customer" ? (
          <Field
            id="org-workspace"
            label="Workspace name"
            name="workspace_name"
            hint="Defaults to the customer's name."
            value={workspaceName}
            onChange={(event) => setWorkspaceName(event.target.value)}
          />
        ) : null}
        <Field
          id="org-owner"
          ref={emailInput}
          label="Owner's email"
          name="owner_email"
          type="email"
          required
          hint="They receive the invitation and become the first owner."
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={emailError}
        />
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Creating…" : "Create and invite the owner"}
          </button>
        </div>
      </form>
    </section>
  );
}
